/**
 * Table des 25 routes de la vague V8e (paquet v8e-p00-socle ; FICHE-CONTRATS-V8E.md § 3).
 *
 * Données seules (aucun code exécuté au chargement) : le fragment `src/v10/routes/v8e.tsx` la lit, les écrans des
 * paquets p01 à p07 l'emploient pour leurs liens (`cheminVers`), le test du socle la confronte au registre.
 *  - `remplace` : la route reprend une route du socle citée par le `couvre: route:` du YAML de l'écran ;
 *  - `module` : chemin du module d'écran sous `src/v10/ecrans/v8e/` (sans extension), dans la zone du paquet qui le
 *    réalise ; ce module exporte par défaut un composant `({ session }) => JSX.Element`.
 * Tant qu'un module est absent, sa route n'est PAS déclarée (le socle historique continue de servir l'adresse) :
 * aucun écran d'attente ne remplace un écran livré.
 * Dialogues : écrans pleins, chacun sa route (règle de la vague V1) ; le contexte attendu (`attend:` du YAML) est
 * porté par les paramètres du chemin.
 */

export type PaquetV8e =
  | 'projets' | 'programmes' | 'lancements' | 'invitations' | 'delegations' | 'journal' | 'accueil'

export interface RouteV8e {
  /** Identifiant de l'écran (`intentions/donnees/ecrans/<ecran>.yaml`). */
  ecran: string
  chemin: string
  paquet: PaquetV8e
  /** `<paquet>/<Composant>` sous `src/v10/ecrans/v8e/`. */
  module: string
  remplace?: true
}

export const ROUTES_V8E: readonly RouteV8e[] = [
  // v8e-p01-projets-cycle
  { ecran: 'organisation-projets', chemin: '/org/projets', paquet: 'projets', module: 'projets/ProjetsOrganisation', remplace: true },
  { ecran: 'dialogue-creer-projet', chemin: '/org/projets/nouveau', paquet: 'projets', module: 'projets/DialogueCreerProjet' },
  { ecran: 'dialogue-renommer-projet', chemin: '/org/projets/:projetId/renommer', paquet: 'projets', module: 'projets/DialogueRenommerProjet' },
  { ecran: 'dialogue-archiver-projet', chemin: '/org/projets/:projetId/archiver', paquet: 'projets', module: 'projets/DialogueArchiverProjet' },
  { ecran: 'dialogue-supprimer-projet', chemin: '/org/projets/:projetId/supprimer', paquet: 'projets', module: 'projets/DialogueSupprimerProjet' },
  { ecran: 'organisation-projet-suppression-fiche', chemin: '/org/suppressions/:demandeId', paquet: 'projets', module: 'projets/SuppressionFiche', remplace: true },
  { ecran: 'dialogue-annuler-suppression-projet', chemin: '/org/suppressions/:demandeId/annuler', paquet: 'projets', module: 'projets/DialogueAnnulerSuppression' },
  // v8e-p02-programmes
  { ecran: 'organisation-programmes', chemin: '/programs', paquet: 'programmes', module: 'programmes/ProgrammesOrganisation', remplace: true },
  { ecran: 'dialogue-creer-programme', chemin: '/org/programmes/nouveau', paquet: 'programmes', module: 'programmes/DialogueCreerProgramme' },
  { ecran: 'dialogue-rattacher-projet-programme', chemin: '/org/projets/:projetId/programme', paquet: 'programmes', module: 'programmes/DialogueRattacherProjet' },
  // v8e-p03-lancements
  { ecran: 'organisation-lancements', chemin: '/org/lancements', paquet: 'lancements', module: 'lancements/Lancements' },
  { ecran: 'organisation-lancement-projet', chemin: '/org/lancements/:projetId', paquet: 'lancements', module: 'lancements/LancementProjet' },
  // v8e-p04-invitations-appartenances
  { ecran: 'organisation-invitation-fiche', chemin: '/org/invitations/:invitationId', paquet: 'invitations', module: 'invitations/FicheInvitation' },
  { ecran: 'dialogue-ajouter-attribution-invitation', chemin: '/org/invitations/:invitationId/attributions/nouvelle', paquet: 'invitations', module: 'invitations/DialogueAjouterAttribution' },
  { ecran: 'dialogue-retirer-attribution-invitation', chemin: '/org/invitations/:invitationId/attributions/:attributionId/retirer', paquet: 'invitations', module: 'invitations/DialogueRetirerAttribution' },
  { ecran: 'dialogue-retirer-appartenance', chemin: '/org/utilisateurs/:userId/appartenances/:portee/:porteeId/retirer', paquet: 'invitations', module: 'invitations/DialogueRetirerAppartenance' },
  // v8e-p05-delegations
  { ecran: 'organisation-delegations', chemin: '/org/delegations', paquet: 'delegations', module: 'delegations/Delegations' },
  { ecran: 'organisation-delegation-fiche', chemin: '/org/delegations/:delegationId', paquet: 'delegations', module: 'delegations/FicheDelegation', remplace: true },
  { ecran: 'dialogue-declarer-delegation', chemin: '/org/delegations/nouvelle', paquet: 'delegations', module: 'delegations/DialogueDeclarerDelegation' },
  // v8e-p06-journal
  { ecran: 'organisation-journal', chemin: '/org/conformite', paquet: 'journal', module: 'journal/JournalOrganisation', remplace: true },
  // v8e-p07-accueil-fiche
  { ecran: 'organisation-accueil-administration', chemin: '/administration', paquet: 'accueil', module: 'accueil/AccueilAdministration', remplace: true },
  { ecran: 'organisation-premiers-pas', chemin: '/org/premiers-pas', paquet: 'accueil', module: 'accueil/PremiersPas' },
  { ecran: 'organisation-fiche', chemin: '/org/fiche', paquet: 'accueil', module: 'accueil/FicheOrganisation', remplace: true },
  { ecran: 'organisation-vue-d-ensemble', chemin: '/tableau-de-bord', paquet: 'accueil', module: 'accueil/VueDEnsemble', remplace: true },
  { ecran: 'dialogue-supprimer-categorie', chemin: '/org/categories/:categorieId/supprimer', paquet: 'accueil', module: 'accueil/DialogueSupprimerCategorie' },
]

/** Clé i18n du titre d'un écran (fragment `v8e`). */
export function cleTitreEcran(ecran: string): string {
  return `v8e.ecran.${ecran}.titre`
}

/**
 * Adresse d'un écran V8e, paramètres encodés : `cheminVers('dialogue-renommer-projet', { projetId: 'p 1' })`
 * → `/org/projets/p%201/renommer`. Écran inconnu ou paramètre manquant : erreur (un lien faux ne part jamais).
 */
export function cheminVers(ecran: string, parametres: Readonly<Record<string, string>> = {}): string {
  const route = ROUTES_V8E.find((candidate) => candidate.ecran === ecran)
  if (!route) throw new Error(`Écran V8e inconnu : ${ecran}`)
  return route.chemin.replace(/:([A-Za-z]\w*)/g, (_, nom: string) => {
    const valeur = parametres[nom]
    if (valeur === undefined || valeur === '') throw new Error(`Paramètre « ${nom} » manquant pour ${ecran}`)
    return encodeURIComponent(valeur)
  })
}
